import { useState, useEffect, useCallback } from 'react'
import { Target, Plus, Trash2, CheckCircle2, Circle, RefreshCw, X, Save,
  AlertTriangle, ArrowUp, ArrowDown, Minus, Clock, User, Check, ShieldCheck } from 'lucide-react'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

interface Mission {
  id: string
  title: string
  description?: string | null
  assignedToIds: string[]
  assignedToNames: string[]
  periodStart?: string | null
  periodEnd?: string | null
  priority: string
  status: string
  completedAt?: string | null
  completedByName?: string | null
  createdAt: string
}

interface Employee {
  id: string
  firstName: string
  lastName: string
  status: string
}

const PRIORITY_OPTIONS = [
  { value: 'urgent', label: 'Urgent',  color: 'text-red-600',    bg: 'bg-red-100',    icon: AlertTriangle },
  { value: 'high',   label: 'Haute',   color: 'text-orange-600', bg: 'bg-orange-100', icon: ArrowUp },
  { value: 'normal', label: 'Normale', color: 'text-blue-600',   bg: 'bg-blue-100',   icon: Minus },
  { value: 'low',    label: 'Basse',   color: 'text-gray-500',   bg: 'bg-gray-100',   icon: ArrowDown },
]

const inp = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500'
const lbl = 'block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'

function formatPeriod(start?: string | null, end?: string | null) {
  const fmt = (d: string) => new Date(d + 'T12:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })
  if (start && end) return `${fmt(start)} → ${fmt(end)}`
  if (end)   return `jusqu'au ${fmt(end)}`
  if (start) return `à partir du ${fmt(start)}`
  return null
}

export default function Missions() {
  const { current } = useEstablishment()
  const selectedEstablishmentId = current?.id
  const [missions, setMissions] = useState<Mission[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [loading, setLoading] = useState(false)
  const [tab, setTab] = useState<'todo' | 'done'>('todo')
  const [showForm, setShowForm] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    title: '', description: '', assignedToIds: [] as string[],
    periodStart: '', periodEnd: '', priority: 'normal'
  })

  const isAdminUp = ['super_admin', 'admin'].includes(JSON.parse(localStorage.getItem('user') || '{}').role)

  // Écarts de l'audit 2026 du magasin → missions à clôturer avant le 31/12 (sans doublon)
  async function importAudit() {
    if (!selectedEstablishmentId || !current) return
    if (!confirm(`Importer les écarts de l'audit 2026 de ${current.name} en missions (échéance 31/12/2026) ?
Les missions déjà présentes ne sont pas recréées.`)) return
    try {
      const r = await api.post('/qse/seed/missions', { establishmentId: selectedEstablishmentId })
      alert(r.data.created ? `✅ ${r.data.created} mission(s) créée(s)` : 'Déjà importé, rien de nouveau')
      load()
    } catch (e: any) { alert(e?.response?.data?.error || "Erreur lors de l'import") }
  }

  const load = useCallback(async () => {
    if (!selectedEstablishmentId) return
    setLoading(true)
    try {
      const [mR, eR] = await Promise.all([
        api.get(`/missions?establishmentId=${selectedEstablishmentId}`),
        api.get(`/employees?establishmentId=${selectedEstablishmentId}`)
      ])
      setMissions(mR.data)
      setEmployees(eR.data.filter((e: Employee) => e.status === 'active'))
    } catch { } finally { setLoading(false) }
  }, [selectedEstablishmentId])

  useEffect(() => {
    load()
    const interval = setInterval(load, 30000)
    const onVisible = () => { if (document.visibilityState === 'visible') load() }
    document.addEventListener('visibilitychange', onVisible)
    return () => { clearInterval(interval); document.removeEventListener('visibilitychange', onVisible) }
  }, [load])

  const pending = missions.filter(m => m.status === 'todo')
  const done    = missions.filter(m => m.status === 'done')

  function toggleEmployee(id: string) {
    setForm(f => ({
      ...f,
      assignedToIds: f.assignedToIds.includes(id)
        ? f.assignedToIds.filter(x => x !== id)
        : [...f.assignedToIds, id]
    }))
  }

  async function create() {
    if (!form.title.trim()) return
    setSaving(true)
    try {
      const assignedToNames = form.assignedToIds
        .map(id => employees.find(e => e.id === id))
        .filter(Boolean)
        .map(e => e!.firstName)
      await api.post('/missions', {
        establishmentId: selectedEstablishmentId,
        title: form.title.trim(),
        description: form.description.trim() || null,
        assignedToIds: form.assignedToIds,
        assignedToNames,
        periodStart: form.periodStart || null,
        periodEnd:   form.periodEnd   || null,
        priority: form.priority,
      })
      setForm({ title: '', description: '', assignedToIds: [], periodStart: '', periodEnd: '', priority: 'normal' })
      setShowForm(false)
      await load()
    } catch { } finally { setSaving(false) }
  }

  async function complete(id: string) {
    await api.post(`/missions/${id}/complete`)
    setMissions(prev => prev.map(m => m.id === id ? { ...m, status: 'done', completedAt: new Date().toISOString() } : m))
  }

  async function reopen(id: string) {
    await api.post(`/missions/${id}/reopen`)
    setMissions(prev => prev.map(m => m.id === id ? { ...m, status: 'todo', completedAt: null } : m))
  }

  async function remove(id: string) {
    if (!confirm('Supprimer cette mission ?')) return
    await api.delete(`/missions/${id}`)
    setMissions(prev => prev.filter(m => m.id !== id))
  }

  const today = new Date().toISOString().split('T')[0]
  const isOverdue = (m: Mission) => m.periodEnd && m.periodEnd < today && m.status === 'todo'
  const isToday   = (m: Mission) => m.periodEnd === today

  function priorityCfg(p: string) {
    return PRIORITY_OPTIONS.find(o => o.value === p) || PRIORITY_OPTIONS[2]
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Target size={24} className="text-purple-600" /> Missions
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">Tâches opérationnelles assignées aux employés</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50">
            <RefreshCw size={16} className={`text-gray-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {isAdminUp && (
            <button onClick={importAudit}
              className="flex items-center gap-2 border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 rounded-lg text-sm font-medium">
              <ShieldCheck size={16} /> Écarts d'audit 2026
            </button>
          )}
          <button onClick={() => setShowForm(true)}
            className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Plus size={16} /> Nouvelle mission
          </button>
        </div>
      </div>

      {/* Formulaire */}
      {showForm && (
        <div className="bg-white border border-purple-200 rounded-2xl p-5 mb-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-gray-800">Créer une mission</h2>
            <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
          </div>
          <div className="space-y-4">
            <div>
              <label className={lbl}>Titre *</label>
              <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="Ex: Remplir le frigo yaourts, Mise en rayon bières..."
                className={inp} autoFocus />
            </div>
            <div>
              <label className={lbl}>Description (optionnel)</label>
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Détails, consignes..." rows={2} className={`${inp} resize-none`} />
            </div>

            {/* Assignée à — multi-sélection par prénom */}
            <div>
              <label className={lbl}>Assignée à <span className="normal-case font-normal text-gray-400">(laisser vide = tout le monde)</span></label>
              <div className="flex flex-wrap gap-2 mt-1">
                {employees.map(e => {
                  const selected = form.assignedToIds.includes(e.id)
                  return (
                    <button key={e.id} type="button" onClick={() => toggleEmployee(e.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${
                        selected
                          ? 'bg-purple-600 text-white border-purple-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-purple-400'
                      }`}>
                      {selected && <Check size={12} />}
                      {e.firstName}
                    </button>
                  )
                })}
                {employees.length === 0 && <p className="text-xs text-gray-400 italic">Aucun employé actif</p>}
              </div>
            </div>

            {/* Priorité */}
            <div>
              <label className={lbl}>Priorité</label>
              <div className="flex gap-2">
                {PRIORITY_OPTIONS.map(o => {
                  const PIcon = o.icon
                  return (
                    <button key={o.value} type="button" onClick={() => setForm(f => ({ ...f, priority: o.value }))}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all ${
                        form.priority === o.value
                          ? `${o.bg} ${o.color} border-current`
                          : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                      }`}>
                      <PIcon size={12} /> {o.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Période */}
            <div>
              <label className={lbl}>Période</label>
              <div className="flex items-center gap-2">
                <input type="date" value={form.periodStart} onChange={e => setForm(f => ({ ...f, periodStart: e.target.value }))}
                  className={`${inp} flex-1`} />
                <span className="text-gray-400 text-sm font-medium">→</span>
                <input type="date" value={form.periodEnd} onChange={e => setForm(f => ({ ...f, periodEnd: e.target.value }))}
                  className={`${inp} flex-1`} />
              </div>
              <p className="text-xs text-gray-400 mt-1">Début et/ou fin de la période (optionnel)</p>
            </div>
          </div>

          <div className="flex justify-end gap-2 mt-5">
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Annuler</button>
            <button onClick={create} disabled={!form.title.trim() || saving}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
              <Save size={14} /> {saving ? 'Enregistrement...' : 'Créer la mission'}
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl mb-4 w-fit">
        <button onClick={() => setTab('todo')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'todo' ? 'bg-white text-purple-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          À faire <span className="ml-1 bg-purple-100 text-purple-700 text-xs px-1.5 py-0.5 rounded-full">{pending.length}</span>
        </button>
        <button onClick={() => setTab('done')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${tab === 'done' ? 'bg-white text-green-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
          Terminées <span className="ml-1 bg-green-100 text-green-700 text-xs px-1.5 py-0.5 rounded-full">{done.length}</span>
        </button>
      </div>

      {/* Liste */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">
          <RefreshCw size={32} className="mx-auto mb-2 animate-spin text-purple-500" />
        </div>
      ) : (
        <div className="space-y-2">
          {(tab === 'todo' ? pending : done).length === 0 ? (
            <div className="text-center py-16 text-gray-400 bg-white rounded-2xl border">
              {tab === 'todo'
                ? <><Target size={48} className="mx-auto mb-2 text-gray-200" /><p>Aucune mission à faire</p></>
                : <><CheckCircle2 size={48} className="mx-auto mb-2 text-gray-200" /><p>Aucune mission terminée</p></>}
            </div>
          ) : (tab === 'todo' ? pending : done).map(m => {
            const pc = priorityCfg(m.priority)
            const PIcon = pc.icon
            const overdue = isOverdue(m)
            const todayDue = isToday(m)
            const period = formatPeriod(m.periodStart, m.periodEnd)
            return (
              <div key={m.id} className={`bg-white rounded-xl border px-4 py-3 flex items-center gap-3 hover:shadow-sm transition-shadow ${
                m.status === 'done' ? 'opacity-70' : overdue ? 'border-red-300 bg-red-50/30' : todayDue ? 'border-purple-300' : 'border-gray-200'
              }`}>
                <button onClick={() => m.status === 'todo' ? complete(m.id) : reopen(m.id)} className="flex-shrink-0">
                  {m.status === 'done'
                    ? <CheckCircle2 size={22} className="text-green-500" />
                    : <Circle size={22} className="text-gray-300 hover:text-purple-400 transition-colors" />}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium text-sm ${m.status === 'done' ? 'line-through text-gray-400' : 'text-gray-900'}`}>{m.title}</p>
                  {m.description && <p className="text-xs text-gray-400 truncate mt-0.5">{m.description}</p>}
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`flex items-center gap-0.5 text-xs font-medium ${pc.color}`}>
                      <PIcon size={10} /> {pc.label}
                    </span>
                    {m.assignedToNames.length > 0 ? (
                      <span className="flex items-center gap-1 text-xs text-gray-600">
                        <User size={10} />
                        {m.assignedToNames.join(', ')}
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 italic">Tous</span>
                    )}
                    {period && (
                      <span className={`flex items-center gap-1 text-xs ${overdue ? 'text-red-600 font-medium' : todayDue ? 'text-purple-600 font-medium' : 'text-gray-400'}`}>
                        <Clock size={10} /> {period}
                        {overdue && ' — En retard'}
                        {todayDue && " — Fin aujourd'hui"}
                      </span>
                    )}
                    {m.status === 'done' && m.completedByName && (
                      <span className="text-xs text-green-600">✓ {m.completedByName}</span>
                    )}
                  </div>
                </div>
                <button onClick={() => remove(m.id)} className="flex-shrink-0 text-gray-300 hover:text-red-500 transition-colors p-1">
                  <Trash2 size={15} />
                </button>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
